import { STATUS, TAKEN, TRXTYPE } from '@siza-staff-care/shared/data';

const Entry_Available = 'Available';
const Entry_Course = 'Course';
const Entry_Leave = 'Leave';
const Entry_LeavePattern = 'LeavePattern';
const Entry_LeavePatternMarker = 'LeavePatternMarker';
const Entry_Shift = 'Shift';
const Entry_Need = 'Need';
const Entry_ShiftRequest = 'ShiftRequest';
const Entry_WorkPattern = 'WorkPattern';
const Entry_Unavailable = 'Unavailable';
const Entry_WorkPatternMarker = 'WorkPatternMarker';

export const getRotaCellCssClass = (
	entry: {
		multiple: boolean;
		otherUnit: boolean;
		type: string;
		isShiftReq: boolean;
		isCustomColour: boolean;
		isActual: boolean;
		adjustBal: boolean;
		status: string;
	},
	forDetails = false,
) => {
	if (!forDetails && entry.multiple) {
		return 'multiple';
	}

	const classes: string[] = [];

	if (entry.otherUnit) {
		classes.push('other');
	}

	if (entry.isShiftReq) {
		classes.push('shiftreq');
		if (entry.status === STATUS.APPROVED) {
			classes.push('approved');
		}
	} else if (entry.type === TRXTYPE.SHIFT) {
		if (!entry.isCustomColour) {
			classes.push('shift');
			if (entry.isActual) {
				classes.push('actual');
			}
			if (!entry.adjustBal) {
				classes.push('bank');
			}
		}
	} else if (entry.type === TRXTYPE.WORK_PATTERN) {
		if (entry.isActual) {
			classes.push('shift');
			classes.push('actual');
		} else {
			classes.push('workpatt');
		}
	} else if (entry.type === TRXTYPE.LEAVE) {
		classes.push('leave');
		switch (entry.status) {
			case STATUS.APPROVED:
				classes.push('approved');
				break;
			case STATUS.OVERRIDDEN:
				classes.push('overridden');
				break;
			case STATUS.PENDING:
				classes.push('pending');
				if (!entry.adjustBal) {
					classes.push('bank');
				}
				break;
			case STATUS.REFUSED:
				classes.push('refused');
				break;
			case TAKEN.NOT_TAKEN:
				classes.push('nottaken');
				break;
			case TAKEN.TAKEN:
				classes.push('taken');
				break;
		}
	} else if (entry.type === TRXTYPE.UNAVAILABLE_HARD) {
		classes.push('unavailable hard');
	} else if (entry.type === TRXTYPE.UNAVAILABLE_SOFT) {
		classes.push('unavailable soft');
	} else if (entry.type === TRXTYPE.AVAILABLE) {
		classes.push('available');
	}

	return classes.join(' ');
};

/**
 * Gets a CSS class for a cell given various parameters.
 * @param  {String} trxType
 * @param  {String} status
 * @param  {String} takenStatus
 * @param  {Boolean} adjustBalance
 * @param  {Boolean} isShiftRequest
 * @param  {Boolean} overridden
 * @param  {Boolean} isActual
 */
export const getCellCssClass = (opts: {
	trxType?: string;
	status?: string;
	takenStatus?: string;
	adjustBalance?: boolean;
	isShiftRequest?: boolean;
	overridden?: boolean;
	isActual?: boolean;
}) => {
	const takenStatus = opts.takenStatus || TAKEN.UNKNOWN;
	const adjustBalance = opts.adjustBalance ?? true;

	if (opts.isShiftRequest) {
		if (opts.overridden) {
			return 'cell-leave-overridden';
		} else if (opts.status === STATUS.APPROVED) {
			return 'cell-shift-request-approved';
		} else {
			return 'cell-shift-request';
		}
	}

	switch (opts.trxType) {
		case TRXTYPE.SHIFT:
			if (opts.isActual) {
				return 'cell-shift-actual';
			} else {
				return adjustBalance ? 'cell-shift' : 'cell-shift-bank';
			}
		case TRXTYPE.WORK_PATTERN:
			if (opts.isActual) {
				return 'cell-shift-actual';
			} else {
				return 'cell-shift-wp';
			}
		case TRXTYPE.LEAVE:
			// Make status the taken status if it's not unknown.
			if (takenStatus !== TAKEN.UNKNOWN) {
				opts.status = takenStatus;
			}
			switch (opts.status) {
				case STATUS.APPROVED:
					return 'cell-leave-approved';
				case STATUS.OVERRIDDEN:
					return 'cell-leave-overridden';
				case STATUS.PENDING:
					return adjustBalance ? 'cell-leave-pending' : 'cell-leave-pending-bank';
				case STATUS.REFUSED:
					return 'cell-leave-refused';
				case TAKEN.NOT_TAKEN:
					return 'cell-leave-not-taken';
				case TAKEN.TAKEN:
					return 'cell-leave-taken';
			}
			break;
		case TRXTYPE.UNAVAILABLE_HARD:
			return 'cell-unavailable-hard';
		case TRXTYPE.UNAVAILABLE_SOFT:
			return 'cell-unavailable-soft';
		case TRXTYPE.AVAILABLE:
			return 'cell-available';
		default:
			return '';
	}

	return '';
};

export const translateRotaCellEntryType = (entryType: string) => {
	switch (entryType) {
		case Entry_Available:
			return 'Availability';
		case Entry_Course:
			return 'Course';
		case Entry_Leave:
			return 'Leave';
		case Entry_LeavePattern:
			return 'Leave (patt.)';
		case Entry_LeavePatternMarker:
			return 'LeavePatternMarker';
		case Entry_Shift:
		case Entry_Need:
			return 'Shift';
		case Entry_ShiftRequest:
			return 'Shift request';
		case Entry_WorkPattern:
			return 'Shift (patt.)';
		case Entry_Unavailable:
			return 'Unavailable';
		case Entry_WorkPatternMarker:
			return 'WorkPatternMarker';
	}

	return '';
};

/**
 * Translates a TrxType into text.
 * @param  {String} trxType
 */
export const translateTrxType = (trxType: string) => {
	switch (trxType) {
		case TRXTYPE.SHIFT:
			return 'Shift';
		case TRXTYPE.WORK_PATTERN:
			return 'Work patt.';
		case TRXTYPE.LEAVE:
			return 'Leave';
		case TRXTYPE.UNAVAILABLE_HARD:
			return 'Time off';
		case TRXTYPE.UNAVAILABLE_SOFT:
			return 'Unavailable';
		case TRXTYPE.AVAILABLE:
			return 'Available';
	}

	return '';
};

/**
 * Determines whether a trxType is a shift or leave.
 * @param  {String} trxType
 */
export const isTrxTypeLeaveOrShift = (trxType: string) => {
	switch (trxType) {
		case TRXTYPE.SHIFT:
		case TRXTYPE.WORK_PATTERN:
		case TRXTYPE.LEAVE:
			return true;
	}

	return false;
};
